import { useCallback, useMemo, useState } from "react";
import {
  buildStreamSearchCriteriaState,
  buildStreamSearchParams,
  type StreamSearchParams,
  type StreamSearchCriteriaState,
  createEmptyStreamSearchCriteriaState,
} from "@/lib/utils/streamSearchUtils";

export type SearchFilters = Pick<
  StreamSearchParams,
  "from" | "to" | "authorId"
>;
export type SearchFilterKey = keyof SearchFilters;

interface UseStreamSearchFormStateOptions {
  onSearch: (searchCriteriaState: StreamSearchCriteriaState) => void;
}

interface UseStreamSearchFormStateResult {
  query: string;
  filters: SearchFilters;
  lastSearchedCriteriaState: StreamSearchCriteriaState;
  hasSearched: boolean;
  searchCriteriaState: StreamSearchCriteriaState;
  setQuery: (query: string) => void;
  submitSearch: () => void;
  clearQuery: () => void;
  updateFilter: (key: SearchFilterKey, value: string) => void;
  clearFilter: (key: SearchFilterKey) => void;
}

export const useStreamSearchFormState = ({
  onSearch,
}: UseStreamSearchFormStateOptions): UseStreamSearchFormStateResult => {
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState<SearchFilters>({});
  const [lastSearchedCriteriaState, setLastSearchedCriteriaState] =
    useState<StreamSearchCriteriaState>(createEmptyStreamSearchCriteriaState());
  const [hasSearched, setHasSearched] = useState(false);

  const searchCriteriaState = useMemo(
    () =>
      buildStreamSearchCriteriaState(buildStreamSearchParams(query, filters)),
    [query, filters],
  );

  const resetSearchState = useCallback(() => {
    setLastSearchedCriteriaState(createEmptyStreamSearchCriteriaState());
    setHasSearched(false);
  }, []);

  const setQueryState = useCallback(
    (nextQuery: string) => {
      setQuery(nextQuery);
      resetSearchState();
    },
    [resetSearchState],
  );

  const submitSearch = useCallback(() => {
    const { hasCriteria: hasSearchCriteria, criteria } = searchCriteriaState;
    if (!hasSearchCriteria) return;
    onSearch(searchCriteriaState);
    setLastSearchedCriteriaState({
      criteria,
      hasCriteria: hasSearchCriteria,
    });
    setHasSearched(true);
  }, [onSearch, searchCriteriaState]);

  const clearQuery = useCallback(() => {
    setQueryState("");
  }, [setQueryState]);

  const updateFilter = useCallback(
    (key: SearchFilterKey, value: string) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value || undefined,
      }));
      resetSearchState();
    },
    [resetSearchState],
  );

  const clearFilter = useCallback(
    (key: SearchFilterKey) => {
      updateFilter(key, "");
    },
    [updateFilter],
  );

  return {
    query,
    filters,
    lastSearchedCriteriaState,
    hasSearched,
    searchCriteriaState,
    setQuery: setQueryState,
    submitSearch,
    clearQuery,
    updateFilter,
    clearFilter,
  };
};
